import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Container,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Paper,
  Divider,
  Fade,
  Slide,
  Grid,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Sidebar, MobileNav } from '@client/components';
import GavelIcon from '@mui/icons-material/Gavel';
import ShieldIcon from '@mui/icons-material/Shield';
import PolicyIcon from '@mui/icons-material/Policy';

const PolicyText = `
# Privacy Policy

**Effective Date:** June 7, 2025
**Company Name:** PyrenzAI

At PyrenzAI, we are committed to protecting your privacy and ensuring your data is handled in a transparent, secure, and lawful manner. This Privacy Policy explains how we collect, use, and manage your information, as well as the rules governing the use of our services.

---

## 1. Eligibility

PyrenzAI services are intended exclusively for users **18 years of age or older**. Minors are not permitted to use our services under any circumstances. By accessing or using our services, you confirm that you are at least 18 years old.

---

## 2. Data Collection and Use

We collect only the information necessary to operate and improve our services. Specifically:

* **Cookies and Tracking:** We use cookies solely for error tracking and service stability purposes. These cookies help us identify issues and enhance performance.
* **GDPR Compliance:** Under the General Data Protection Regulation (GDPR), you have the right to access, correct, or delete your personal data. To exercise these rights, contact us at [support@pyrenzai.com](mailto:support@pyrenzai.com).

We do **not** sell, share, or use personal data for profiling, advertising, or marketing purposes.

---

## 3. User Data Handling and Responsibility

PyrenzAI treats user data (including messages, API keys, and other sensitive information) with strict security standards. However, **PyrenzAI is not responsible** for any data misuse or security incidents resulting from:

* Users mishandling their own data
* Sharing API keys with third parties
* Posting login credentials publicly
* Any negligence in protecting personal information

**User Responsibility:** Users are solely responsible for maintaining the confidentiality and security of their accounts and credentials.

**Account Deletion:** When a user deletes their account, all personal information and content associated with that account will be **permanently deleted** and cannot be recovered.

---

## 4. Refund Policy (Balance System)

PyrenzAI no longer operates on subscriptions. Instead, users purchase **credits** to access services. Refunds are subject to the following conditions:

1. Refund requests may only be submitted **after 72 hours** from the original credit purchase.
2. Refunds require **proof of transaction**, such as a receipt or payment confirmation.
3. Refunds are limited to the value of the purchased credits and are not automatically granted.

To request a refund, please contact our support team at [support@pyrenzai.com](mailto:support@pyrenzai.com) with the necessary documentation.

---

## 5. Prohibited Content

The following content is strictly prohibited on PyrenzAI:

* Pornography of any kind
* Child sexual abuse material (CSAM) or related content
* Any content featuring human-faced characters (real, generated, or synthetic)
* Racist or hate-based content
* Child characters in any context violating child safety policies
* Content involving rape, incest, bestiality, or pedophilia
* Any content promoting or normalizing illegal, violent, or harmful behavior

Violation of these rules may result in **immediate account suspension or termination** and could be reported to authorities when required.

---

## 6. AI-Generated Content Disclaimer

PyrenzAI generates text and other outputs based on user input. While we strive for quality, **we do not guarantee that AI-generated responses are accurate, factual, or suitable for any specific purpose**. Use AI-generated content at your own risk.

**Character Safety & Visibility:** AI-generated characters **may be publicly visible by default**. Users are responsible for marking characters as private if needed. PyrenzAI does not guarantee the safety or appropriateness of generated content.

---

## 7. Policy Updates

This Privacy Policy may be updated periodically. The **effective date** at the top will be revised accordingly. Continued use of PyrenzAI after any update constitutes acceptance of the new terms.

---

## 8. Contact Us

For questions regarding this Privacy Policy, or to exercise your data rights under GDPR, contact:

**PyrenzAI Support Team**
[support@pyrenzai.com](mailto:support@pyrenzai.com)

By using PyrenzAI, you acknowledge and agree to the terms outlined in this Privacy Policy.

`;

export function Policy() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showLoginModal, setShowLoginModal] = useState(false);

  return (
    <>
      {isMobile ? (
        <MobileNav setShowLoginModal={setShowLoginModal} />
      ) : (
        <Sidebar />
      )}

      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%)',
          py: 8,
          px: 2,
        }}
      >
        <Fade in timeout={800}>
          <Container maxWidth="md">
            <Slide direction="up" in timeout={600}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 6 },
                  borderRadius: 4,
                  background: 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                }}
              >
                <Grid container spacing={3} justifyContent="center">
                  <Grid size={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        mb: 4,
                      }}
                    >
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <GavelIcon sx={{ color: 'white', fontSize: 28 }} />
                      </Box>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          backgroundClip: 'text',
                          textFillColor: 'transparent',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          letterSpacing: '-0.02em',
                        }}
                      >
                        Terms of Service
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid size={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        mb: 4,
                        color: 'rgba(255, 255, 255, 0.6)',
                      }}
                    >
                      <ShieldIcon fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Last Updated: March 2026
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid size={12}>
                    <Divider
                      sx={{
                        my: 4,
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                        '&::before, &::after': {
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    />
                  </Grid>

                  <Grid size={12}>
                    <Box
                      sx={{
                        color: 'rgba(255, 255, 255, 0.85)',
                        '& h1': { display: 'none' },
                        '& h2': {
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '1.5rem',
                          mt: 4,
                          mb: 2,
                          pb: 1,
                          borderBottom: '2px solid rgba(102, 126, 234, 0.3)',
                          display: 'inline-block',
                        },
                        '& p': {
                          lineHeight: 1.8,
                          mb: 2,
                          color: 'rgba(255, 255, 255, 0.7)',
                        },
                        '& ul': {
                          pl: 0,
                          listStyle: 'none',
                          mb: 3,
                        },
                        '& li': {
                          mb: 1.5,
                          color: 'rgba(255, 255, 255, 0.7)',
                          position: 'relative',
                          pl: 3,
                          '&::before': {
                            content: '"▸"',
                            position: 'absolute',
                            left: 0,
                            color: '#667eea',
                            fontWeight: 'bold',
                          },
                        },
                      }}
                    >
                      <ReactMarkdown
                        components={{
                          h1: () => null,
                          h2: ({ node, ...props }) => {
                            const { ref, ...rest } = props;
                            return (
                              <Typography
                                variant="h5"
                                component="h2"
                                gutterBottom
                                {...rest}
                              />
                            );
                          },
                          p: ({ node, ...props }) => {
                            const { ref, ...rest } = props;
                            return (
                              <Typography
                                variant="body1"
                                component="p"
                                paragraph
                                {...rest}
                              />
                            );
                          },
                          ul: ({ node, ...props }) => {
                            const { ref, ...rest } = props;
                            return (
                              <List
                                dense
                                disablePadding
                                {...rest}
                                sx={{
                                  listStyle: 'none',
                                  pl: 0,
                                  mb: 3,
                                }}
                              />
                            );
                          },
                          li: ({ node, children, ...props }) => {
                            const { ref, ...rest } = props;
                            return (
                              <ListItem
                                disablePadding
                                sx={{
                                  mb: 1.5,
                                  position: 'relative',
                                  pl: 3,
                                  '&::before': {
                                    content: '"▸"',
                                    position: 'absolute',
                                    left: 0,
                                    color: '#667eea',
                                    fontWeight: 'bold',
                                  },
                                }}
                                {...rest}
                              >
                                <ListItemText
                                  primary={
                                    <Typography
                                      component="span"
                                      variant="body1"
                                      sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                                    >
                                      {children}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            );
                          },
                        }}
                      >
                        {PolicyText}
                      </ReactMarkdown>
                    </Box>
                  </Grid>

                  <Grid size={12}>
                    <Divider
                      sx={{
                        my: 4,
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      }}
                    />
                  </Grid>

                  <Grid size={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        color: 'rgba(255, 255, 255, 0.5)',
                      }}
                    >
                      <PolicyIcon fontSize="small" />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Pyrenz AI © 2026. All rights reserved.
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Slide>
          </Container>
        </Fade>
      </Box>
    </>
  );
}